import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import * as api from '../../api';
import type { SharedWithUser } from '../../api';

interface ManageSharesDialogProps {
  open: boolean;
  onClose: () => void;
  tripName?: string;
  sharedWith: SharedWithUser[];
  onSharesUpdated: () => void;
}

function ManageSharesDialog({
  open,
  onClose,
  tripName,
  sharedWith,
  onSharesUpdated
}: ManageSharesDialogProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localSharedWith, setLocalSharedWith] = useState<SharedWithUser[]>(sharedWith);

  // Sync local state when prop changes
  useEffect(() => {
    setLocalSharedWith(sharedWith);
  }, [sharedWith]);

  const handleClose = () => {
    setError(null);
    setLoading(null);
    onClose();
  };

  const handleTogglePermission = async (user: SharedWithUser) => {
    setError(null);
    setLoading(user.share_token);

    // Optimistically update local state
    const newCanEdit = !user.can_edit;
    setLocalSharedWith(prev =>
      prev.map(u =>
        u.share_token === user.share_token ? { ...u, can_edit: newCanEdit } : u
      )
    );

    try {
      await api.updateTripShare({
        share_token: user.share_token,
        can_edit: newCanEdit,
      });
      // Refresh in background without blocking UI
      onSharesUpdated();
    } catch (err) {
      // Revert on error
      setLocalSharedWith(prev =>
        prev.map(u =>
          u.share_token === user.share_token ? { ...u, can_edit: user.can_edit } : u
        )
      );
      setError(err instanceof Error ? err.message : 'Failed to update permission');
    } finally {
      setLoading(null);
    }
  };

  const handleRemoveShare = async (user: SharedWithUser) => {
    setError(null);
    setLoading(user.share_token);

    // Optimistically remove from local state
    setLocalSharedWith(prev => prev.filter(u => u.share_token !== user.share_token));

    try {
      await api.removeTripShare({
        share_token: user.share_token,
      });
      // Refresh in background without blocking UI
      onSharesUpdated();
    } catch (err) {
      // Revert on error
      setLocalSharedWith(sharedWith);
      setError(err instanceof Error ? err.message : 'Failed to remove share');
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOutlineIcon sx={{ color: '#bdbdbd' }} />
          <Typography variant="h6" component="span" fontWeight={600}>
            Manage Sharing
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {tripName && (
          <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Trip
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {tripName}
            </Typography>
          </Box>
        )}

        {localSharedWith.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              This trip hasn't been shared with anyone yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Shared with {localSharedWith.length} {localSharedWith.length === 1 ? 'person' : 'people'}
            </Typography>
            {localSharedWith.map((user, index) => (
              <Box key={user.share_token}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <PersonOutlineIcon sx={{ color: '#bdbdbd', fontSize: 28 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {user.email}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {user.can_edit ? (
                          <>
                            <EditIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography variant="caption" color="success.main">
                              Can edit
                            </Typography>
                          </>
                        ) : (
                          <>
                            <VisibilityIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              View only
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch
                      checked={user.can_edit}
                      onChange={() => handleTogglePermission(user)}
                      disabled={loading === user.share_token}
                      size="small"
                    />
                    <IconButton
                      onClick={() => handleRemoveShare(user)}
                      disabled={loading === user.share_token}
                      size="small"
                      sx={{
                        color: 'error.main',
                        '&:hover': { bgcolor: 'error.light' }
                      }}
                    >
                      {loading === user.share_token ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" sx={{ color: 'grey.600' }} />
                      )}
                    </IconButton>
                  </Box>
                </Box>
                {index < localSharedWith.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="contained" color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ManageSharesDialog;
